import React from 'react';
import PropTypes from 'prop-types';
import Platform from "react-native-web/dist/exports/Platform";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import View from "react-native-web/dist/exports/View";
import InputLabel from '../InputLabel';
import ButtonBase from '../Button/ButtonBase';
import StackView from '../StackView';
import { useThemeTokensCallback, applyShadowToken } from '../ThemeProvider';
import { a11yProps, focusHandlerProps, getTokensPropType, selectTokens, pressProps, selectSystemProps, useUniqueId, variantProp, viewProps } from '../utils';
import { useInputValue } from '../utils/input';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, focusHandlerProps, pressProps, viewProps]);

// We need to drop the icon before passing it to the `ButtonBase`, because it's
// being handled separately in this case
const selectButtonTokens = (_ref, _ref2) => {
  let {
    icon: _,
    ...tokens
  } = _ref;
  let {
    trackHeight,
    outerBorderGapButton
  } = _ref2;
  return selectTokens('Button', {
    ...tokens,
    // Width tokens are applied to our inner track. Disable Button width token so it wraps our track width.
    outerBorderGap: outerBorderGapButton,
    height: trackHeight,
    width: null
  });
};

// Map and rename icon-specific tokens to name used within Icon
const selectIconTokens = _ref3 => {
  let {
    iconSize,
    iconColor
  } = _ref3;
  return {
    size: iconSize,
    color: iconColor
  };
};
const selectTrackSwitchStyles = _ref4 => {
  let {
    switchSize,
    width,
    trackHeight
  } = _ref4;
  return {
    width,
    height: Math.max(switchSize, trackHeight)
  };
};
const selectTrackStyles = _ref5 => {
  let {
    trackBorderWidth,
    trackBorderColor,
    trackBorderRadius,
    width
  } = _ref5;
  return {
    borderWidth: trackBorderWidth,
    borderColor: trackBorderColor,
    borderRadius: trackBorderRadius,
    width
  };
};
const selectSwitchStyles = _ref6 => {
  let {
    switchSize,
    switchColor,
    switchBorderWidth,
    switchBorderColor,
    switchBorderRadius,
    switchShadow
  } = _ref6;
  return {
    position: 'absolute',
    width: switchSize,
    height: switchSize,
    backgroundColor: switchColor,
    borderWidth: switchBorderWidth,
    borderColor: switchBorderColor,
    borderRadius: switchBorderRadius,
    ...applyShadowToken(switchShadow),
    ...Platform.select({
      // TODO: https://github.com/telus/universal-design-system/issues/487
      web: {
        transition: 'transform 200ms'
      }
    })
  };
};
const selectMobileTokens = tokens => ({
  ...Platform.select({
    web: {},
    default: {
      switchSize: tokens?.mobileSwitchSize,
      trackHeight: tokens?.mobileTrackHeight,
      width: tokens?.mobileWidth,
      trackBorderWidth: tokens?.mobileTrackBorderWidth
    }
  })
});
const selectLabelStyles = _ref7 => {
  let {
    labelMarginLeft
  } = _ref7;
  return {
    marginLeft: labelMarginLeft
  };
};
const selectLabelTokens = _ref8 => {
  let {
    labelColor,
    labelFontName,
    labelFontSize,
    labelFontWeight,
    labelLineHeight
  } = _ref8;
  return {
    color: labelColor,
    fontName: labelFontName,
    fontWeight: labelFontWeight,
    fontSize: labelFontSize,
    lineHeight: labelLineHeight
  };
};
const ToggleSwitch = /*#__PURE__*/React.forwardRef((_ref9, ref) => {
  let {
    copy = 'en',
    value,
    initialValue,
    onChange,
    id,
    label,
    inactive,
    tokens,
    tooltip,
    variant,
    accessibilityRole = 'switch',
    accessibilityLabel = label,
    togglePosition = 'end',
    ...rest
  } = _ref9;
  const getTokens = useThemeTokensCallback('ToggleSwitch', tokens, variant);
  const themeTokens = getTokens();
  const {
    currentValue,
    setValue
  } = useInputValue({
    value,
    initialValue,
    onChange
  });
  const handlePress = event => setValue(!currentValue, event);
  const getButtonTokens = buttonState => selectButtonTokens(getTokens(buttonState, selectMobileTokens(themeTokens)), getTokens(themeTokens, selectMobileTokens(themeTokens)));
  const uniqueId = useUniqueId('toggleSwitch');
  const inputId = id ?? uniqueId;
  return /*#__PURE__*/_jsxs(StackView, {
    space: themeTokens.space,
    direction: togglePosition === 'start' ? 'row-reverse' : 'row',
    tokens: {
      alignItems: 'center',
      justifyContent: themeTokens.containerWidth ? 'space-between' : 'start',
      width: themeTokens.containerWidth
    },
    children: [Boolean(label) && /*#__PURE__*/_jsx(View, {
      style: [selectLabelStyles(themeTokens), staticStyles.containText],
      children: /*#__PURE__*/_jsx(InputLabel, {
        copy: copy,
        forId: inputId,
        label: label,
        tokens: selectLabelTokens(themeTokens),
        tooltip: tooltip
      })
    }), /*#__PURE__*/_jsx(ButtonBase, {
      id: id,
      ref: ref,
      selected: currentValue,
      inactive: inactive,
      tokens: getButtonTokens,
      accessibilityLabel: accessibilityLabel,
      accessibilityRole: accessibilityRole,
      accessibilityState: {
        checked: currentValue
      },
      onPress: handlePress,
      ...selectProps(rest),
      children: buttonState => {
        const stateTokens = getTokens(buttonState, selectMobileTokens(themeTokens));
        const IconComponent = stateTokens.icon;
        const switchStyles = selectSwitchStyles(stateTokens);
        const trackStyles = selectTrackStyles(stateTokens);
        const iconTokens = selectIconTokens(stateTokens);
        const trackSwitchStyles = selectTrackSwitchStyles(stateTokens);
        const {
          switchSize,
          trackHeight,
          width,
          trackBorderWidth
        } = stateTokens;
        // If drag-slide support is needed, use a PanResponder and apply these to an Animated value.
        // Use translate transforms for smoothest non-thread-blocking animations and to allow drag.
        // Two different translates depending on the switchSize and trackSize relationship.
        const isSwitchTallerThanTrack = switchSize >= trackHeight;
        const slideStart = isSwitchTallerThanTrack ? 0 : trackBorderWidth;
        const slideEnd = isSwitchTallerThanTrack ? width - switchSize : width - switchSize - trackBorderWidth;
        const switchOffset = buttonState.selected ? slideEnd : slideStart;
        const switchPositionStyle = {
          transform: [{
            translateX: switchOffset
          }]
        };
        return /*#__PURE__*/_jsxs(View, {
          nativeID: "trackSwitch",
          style: [trackSwitchStyles, staticStyles.trackSwitch],
          children: [/*#__PURE__*/_jsx(View, {
            style: [staticStyles.track, trackStyles]
          }), /*#__PURE__*/_jsx(View, {
            style: [staticStyles.switch, switchStyles, switchPositionStyle],
            children: IconComponent && /*#__PURE__*/_jsx(IconComponent, {
              ...iconTokens
            })
          })]
        });
      }
    })]
  });
});
ToggleSwitch.displayName = 'ToggleSwitch';
ToggleSwitch.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Whether the English or French copy will be used (e.g. for accessibility labels).
   */
  copy: PropTypes.oneOf(['en', 'fr']),
  tokens: getTokensPropType('ToggleSwitch'),
  variant: variantProp.propType,
  /**
   * If this is a "controlled" component and a parent handles its selected state,
   * tells the switch if it is on (true) or off (false).
   */
  value: PropTypes.bool,
  /**
   * If this is an "uncontrolled" component that handles its own selected state,
   * optionally tells the switch if it should default to on (true) or off (false).
   */
  initialValue: PropTypes.bool,
  /**
   * Function called when the switch is toggled, with one boolean argument that is the
   * new position of the switch, on (true) or off (false). For "controlled" components,
   * this should always be passed and used to control the state of the switch.
   */
  onChange: PropTypes.func,
  /**
   * Input ID.
   */
  id: PropTypes.string,
  /**
   * An optional label.
   */
  label: PropTypes.string,
  /**
   * If passed, the switch does not respond to user input and may recieve different
   * theme tokens if the theme supports inactive appearance.
   */
  inactive: PropTypes.bool,
  /**
   * Content of an optional Tooltip. If set, a tooltip button will be shown next to the label.
   */
  tooltip: PropTypes.string,
  /**
   * Controls the position of the switch in relation to the label.
   * - 'start': switch on the start, before the `label` and `tooltip`.
   * - 'end': switch to the end, after the `label` and `tooltip` (default).
   */
  togglePosition: PropTypes.oneOf(['start', 'end'])
};
const staticStyles = StyleSheet.create({
  track: {
    flexDirection: 'row'
  },
  switch: {
    alignItems: 'center',
    justifyContent: 'center'
  },
  containText: {
    flexShrink: 1
  },
  trackSwitch: {
    flexDirection: 'row',
    alignItems: 'center'
  }
});
export default ToggleSwitch;